// packages/shared-components/src/utils/lightingUtils.ts

import { KeyLightingConfig } from "../components/Keyboard/types";
import {
  KEYCODE_TO_WEB_KEY_MAP,
  KEYBOARD_LAYOUTS,
  isEmptyKey,
} from "./keyCodeMapper";
import { hexToRgb as hexToRgbBase, rgbToHex as rgbToHexBase } from "./color";
import { KEYCODE_MAP_BEIYING } from "./keyCodeMapperBeiying";
import {
  beiying8815LayoutCodeToFirmwareTableKey,
  resolveX98_1KPosForLayoutKey,
} from "./keyCodeMapperBeiying";
import { BEIYING_8815_LAYER0_KEYBOARD_KEYS } from "./x98_1kBeiyingKeyboardRaw";
import { isBeiyingDevice8815X98Keyboard } from "@eweadn-qiankun/shared-utils";
import { KEYCODE_MAP_HFD, KEYCODE_MAP_HFD_LAYOUT } from "./keyCodeMapperHfd";
import type { AllKeyboardType } from "@eweadn-qiankun/shared-types";
import { usesSliceFirstRowLayout } from "./sliceFirstRowKeyboardMatrix";
import { resolveX87HeCustomLightGridCell } from "./x87HeLightingUtils";
import { resolveEs87CustomLightGridCell } from "./es87LightingUtils";

type Es68FamilyLightingConfig = {
  solutionRovider?: string;
  productId?: number;
  vendorId?: number;
};

/** 星闪 ES68 / 68EVO 等：SDK 自定义灯网格 → UI 展示网格（与 useKeyboardLighting 一致） */
export function isXingshanEs68FamilyLightingGrid(
  config?: Es68FamilyLightingConfig
): boolean {
  if (config?.solutionRovider !== "xingshan") return false;
  const productId = Number(config.productId);
  const vendorId = Number(config.vendorId);
  return (
    (productId === 7186 && vendorId === 7334) ||
    (productId === 7211 && vendorId === 7334) ||
    (productId === 24065 && vendorId === 7334) ||
    (productId === 7245 && vendorId === 7334) ||
    (productId === 7241 && vendorId === 7334) ||
    (productId === 7204 && vendorId === 7334) ||
    (productId === 65153 && vendorId === 6790) ||
    (productId === 7215 && vendorId === 7334) ||
    (productId === 7199 && vendorId === 7334) ||
    (productId === 7242 && vendorId === 7334) ||
    (productId === 7244 && vendorId === 7334) ||
    (productId === 7248 && vendorId === 7334)
  );
}

export function transformXingshanEs68FamilyLightingGrid<
  T extends { R?: number; G?: number; B?: number; isCustom?: boolean },
>(data: Array<Array<T>>, config?: Es68FamilyLightingConfig): Array<Array<T>> {
  const newData = JSON.parse(JSON.stringify(data)) as Array<Array<T>>;
  if (!isXingshanEs68FamilyLightingGrid(config)) return newData;
  if (Array.isArray(newData[3])) {
    newData[3][12] = newData[3][13];
  }
  // ES68/DK63/DK68 等 15 列 UI 与 SDK 灯位列一一对应；勿 splice(1,1)，否则 Shift 行虚拟取色左移（选 X 时 Z 也变色）
  return newData;
}

/**
 * ES68 族展示网格列（layout originalCol → keyColors 列）：
 * - 行 3：SDK col13 合并展示到 col12
 * - 行 4：与 layout originalCol 一致（SDK 与 UI 对齐，无 splice 偏移）
 */
export function resolveEs68FamilyLightingGridCol(
  matrixRow: number,
  originalCol: number
): number {
  if (matrixRow === 3 && originalCol === 13) return 12;
  return originalCol;
}

/**
 * ES68 族 UI/layout 列 → SDK 原始灯光矩阵列（setLightingCustom 写入用）。
 * 与历史 LightingOperation 一致：SDK 列号与 layout originalCol 相同（勿 +1，否则 Z 会写到 X 的灯珠）。
 */
export function resolveEs68FamilySdkLightingCol(
  _matrixRow: number,
  uiCol: number
): number {
  return uiCol;
}

/** ES68 族写入 SDK 时需同步的列（行 3 的 Enter：回读合并 col13→12，须双写） */
export function resolveEs68FamilySdkLightingColsForWrite(
  matrixRow: number,
  lightingCol: number
): readonly number[] {
  if (matrixRow === 3 && lightingCol === 12) {
    return [12, 13];
  }
  return [resolveEs68FamilySdkLightingCol(matrixRow, lightingCol)];
}

export function es68FamilySdkLightingColMatchesWrite(
  matrixRow: number,
  sdkColIndex: number,
  lightingRow: number,
  lightingCol: number
): boolean {
  if (matrixRow !== lightingRow) return false;
  return resolveEs68FamilySdkLightingColsForWrite(
    matrixRow,
    lightingCol
  ).includes(sdkColIndex);
}

type CustomLitLayoutKeyLike = {
  originalRow?: number;
  originalCol?: number;
  code?: string;
  keyCode?: number;
};

/** 与 Keyboard.getKeyColor 一致：按 layout 键取自定义灯单元 */
export function resolveCustomLitKeyCell(
  keyColors: Record<string, KeyLightingConfig> | null | undefined,
  key: CustomLitLayoutKeyLike,
  layoutRowIndex: number,
  keyboardType?: string | null
): KeyLightingConfig | undefined {
  if (!keyColors) return undefined;
  const matrixRow =
    key.originalRow != null && key.originalRow >= 0
      ? key.originalRow
      : layoutRowIndex;
  if (key.originalCol == null || key.originalCol < 0) return undefined;

  let lightingCol = key.originalCol;
  if (
    keyboardType === "es87" &&
    key.keyCode != null &&
    Number.isFinite(Number(key.keyCode)) &&
    lightingCol != null
  ) {
    lightingCol = resolveEs87CustomLightGridCell(
      matrixRow,
      Number(key.keyCode),
      lightingCol
    ).col;
  } else if (keyboardType === "87") {
    lightingCol = resolveX87HeCustomLightGridCell(
      matrixRow,
      Number(key.keyCode ?? 0),
      lightingCol
    ).col;
  } else if (usesSliceFirstRowLayout(keyboardType)) {
    lightingCol = resolveEs68FamilyLightingGridCol(matrixRow, lightingCol);
  }

  const matrixColor = keyColors[lightingMatrixKey(matrixRow, lightingCol)];
  const codeColor = key.code ? keyColors[key.code] : undefined;
  const webKeyFromKeyCode =
    key.keyCode != null && Number.isFinite(Number(key.keyCode))
      ? KEYCODE_TO_WEB_KEY_MAP[Number(key.keyCode)]
      : undefined;
  const keyCodeColor = webKeyFromKeyCode
    ? keyColors[webKeyFromKeyCode]
    : undefined;
  const cell = matrixColor ?? codeColor ?? keyCodeColor;
  if (!cell || cell.isCustom !== true) return undefined;
  return customLightCellHasVisibleRgb(cell) ? cell : undefined;
}

export function isCustomLitLayoutKey(
  keyColors: Record<string, KeyLightingConfig> | null | undefined,
  key: CustomLitLayoutKeyLike,
  layoutRowIndex: number,
  keyboardType?: string | null
): boolean {
  return (
    resolveCustomLitKeyCell(keyColors, key, layoutRowIndex, keyboardType)
      ?.isCustom === true
  );
}

/** 与 main-app 贝盈 getLightingCustom 一致：8815 X98 用 X98_1K 矩阵，避免与 UI「98」简表列错位 */
export const resolveBeiyingCustomLightLayoutMatrix = (
  keyboardType?: string,
  equipment?: {
    solutionRovider?: string;
    displayname?: string;
    keyboardType?: string;
  } | null
): number[][] => {
  if (
    equipment &&
    String(equipment.solutionRovider ?? "") === "beiying" &&
    isBeiyingDevice8815X98Keyboard(equipment)
  ) {
    return (
      (KEYBOARD_LAYOUTS as Record<string, number[][]>).X98_1K ??
      KEYBOARD_LAYOUTS["98"]
    );
  }
  const kt = String(keyboardType ?? equipment?.keyboardType ?? "")
    .trim()
    .toLowerCase();
  if (kt === "98" || kt === "x98_1k") {
    return (
      (KEYBOARD_LAYOUTS as Record<string, number[][]>).X98_1K ??
      KEYBOARD_LAYOUTS["98"]
    );
  }
  const layout =
    (KEYBOARD_LAYOUTS as Record<string, number[][]>)[kt] ??
    KEYBOARD_LAYOUTS["99"];
  return layout ?? KEYBOARD_LAYOUTS["99"];
};

/** 8815 自定义灯回包 pos 字段可能是 pos / light_pos / effect_pos */
export const resolveBeiyingCustomLightItemPos = (
  item: unknown
): number | undefined => {
  if (item == null || typeof item !== "object") return undefined;
  const o = item as Record<string, unknown>;
  for (const key of ["pos", "light_pos", "effect_pos", "keyPos", "lightPos"]) {
    const n = Number(o[key]);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
};

/**
 * 8815：布局格 → 矩阵坐标，并注册 pos/light_pos/effect_pos 别名（读回显与下发 pos 一致）
 */
export const build8815PosAliasToRowColMap = (
  keyData: number[][],
  devHidToPos?: Map<number, number>
): Map<number, { row: number; col: number }> => {
  const map = new Map<number, { row: number; col: number }>();
  const register = (p: number | undefined, row: number, col: number) => {
    if (p == null || !Number.isFinite(p)) return;
    if (!map.has(p)) map.set(p, { row, col });
  };

  for (let row = 0; row < keyData.length; row++) {
    const line = keyData[row] ?? [];
    for (let col = 0; col < line.length; col++) {
      const layoutCode = Number(line[col]);
      if (!layoutCode) continue;
      register(resolveX98_1KPosForLayoutKey(layoutCode, devHidToPos), row, col);
      const firmwareKey = beiying8815LayoutCodeToFirmwareTableKey(layoutCode);
      const factory = BEIYING_8815_LAYER0_KEYBOARD_KEYS.find(
        (k) => Number(k.value) === firmwareKey
      );
      if (factory) {
        register(Number(factory.pos), row, col);
        register(Number(factory.light_pos), row, col);
        register(Number(factory.effect_pos), row, col);
      }
    }
  }
  return map;
};

/** 自定义灯 pos 别名 → KEYCODE_MAP_BEIYING.id（web-hid getCustomMainLight 回包补全） */
export const mapBeiyingCustomLightPosToKeycapId = (
  pos: number
): number | undefined => {
  if (!Number.isFinite(pos)) return undefined;
  const byCapId = KEYCODE_MAP_BEIYING.find((k) => Number(k.id) === pos);
  if (byCapId?.id != null) return Number(byCapId.id);
  const byKeycode = KEYCODE_MAP_BEIYING.find(
    (k) => k.keycode === pos || k.xsKeycode === pos
  );
  if (byKeycode?.id != null) return Number(byKeycode.id);
  const factory = BEIYING_8815_LAYER0_KEYBOARD_KEYS.find(
    (k) =>
      Number(k.pos) === pos ||
      Number(k.light_pos) === pos ||
      Number(k.effect_pos) === pos
  );
  if (!factory) return undefined;
  const cap = KEYCODE_MAP_BEIYING.find(
    (k) => k.keycode === factory.value || k.xsKeycode === factory.value
  );
  const id = cap?.id;
  return id != null && Number.isFinite(Number(id)) ? Number(id) : undefined;
};

/**
 * layout HID / xsKeycode 与别键键帽 id 撞号（ES98 `~` HID=53 / `'` id=53），
 * 或布局格为 xsKeycode（X98 计算器 8594 / 键帽 id=3584）：固件常 id=8594 带回 ghost。
 * 此类槽位读色只能 result.find(id === capId)，不能回退 find(id === layoutKeycode/xsKeycode)。
 * （ES98 的 1!/5% 漏色另在 legacy 读色走 index 对齐，见 beiying/lighting.ts）
 */
export function isBeiyingLayoutSlotCapIdOnlyRead(
  layoutKeycode: number,
  capId: number,
  cap?: { xsKeycode?: number; keycode?: number } | null
): boolean {
  if (!Number.isFinite(layoutKeycode) || !Number.isFinite(capId)) return false;
  if (layoutKeycode !== capId) {
    if (KEYCODE_MAP_BEIYING.some((k) => Number(k.id) === layoutKeycode)) {
      return true;
    }
    const xs = cap?.xsKeycode != null ? Number(cap.xsKeycode) : Number.NaN;
    if (Number.isFinite(xs) && xs === layoutKeycode) {
      return true;
    }
  }
  return false;
}

function pickBeiyingCustomLightRawRgb(
  raw: Map<number, { r: number; g: number; b: number }>,
  ...keys: number[]
): { r: number; g: number; b: number } | undefined {
  for (const key of keys) {
    if (!Number.isFinite(key)) continue;
    const rgb = raw.get(key);
    if (!rgb) continue;
    if (isBeiyingCustomFirmwareGhostRgb(rgb.r, rgb.g, rgb.b)) continue;
    return rgb;
  }
  return undefined;
}

/**
 * web-hid（ES98 / X98 8K 等）：getCustomMainLight 回包键可能是键帽 id、HID keycode 或固件 pos。
 * 例如 F5 键帽 id=6、keycode=62，若 SDK 把 62 写入 id 字段，直接 map.set(62) 会误落到 M 键（id=62）。
 */
export const normalizeBeiyingCustomLightIdToColorMap = (
  raw: Map<number, { r: number; g: number; b: number }>
): Map<number, { r: number; g: number; b: number }> => {
  const out = new Map<number, { r: number; g: number; b: number }>();
  for (const cap of KEYCODE_MAP_BEIYING) {
    const id = Number(cap.id);
    if (!Number.isFinite(id)) continue;
    let rgb = pickBeiyingCustomLightRawRgb(
      raw,
      id,
      Number(cap.keycode),
      cap.xsKeycode != null ? Number(cap.xsKeycode) : Number.NaN
    );
    if (!rgb) {
      for (const [rawKey, candidate] of raw) {
        const resolved = mapBeiyingCustomLightPosToKeycapId(rawKey);
        if (resolved === id) {
          if (
            !isBeiyingCustomFirmwareGhostRgb(
              candidate.r,
              candidate.g,
              candidate.b
            )
          ) {
            rgb = candidate;
          }
          break;
        }
      }
    }
    if (rgb) out.set(id, rgb);
  }
  return out;
};

/**
 * RGB 转 HEX（适配器函数）
 * 内部使用 color.ts 的实现
 */
export const rgbToHex = (r: number, g: number, b: number): string => {
  return rgbToHexBase(r, g, b);
};

/**
 * HEX 转 RGB（适配器函数）
 * 返回大写的 R, G, B 以保持向后兼容
 */
export const hexToRGB = (hex: string): { R: number; G: number; B: number } => {
  const { r, g, b } = hexToRgbBase(hex);
  return { R: r, G: g, B: b };
};

/**
 * 应用亮度到 RGB
 */
export const applyBrightness = (
  rgb: KeyLightingConfig,
  brightness: number
): string => {
  const factor = brightness / 100;
  return rgbToHex(
    Math.round(rgb.R * factor),
    Math.round(rgb.G * factor),
    Math.round(rgb.B * factor)
  );
};

/**
 * ✅ 动态生成位置映射（基于 SDK 布局数据）
 * @param layoutData SDK 布局数据 layoutData[row][col] = keyCode
 * @returns 位置映射 { webKey: [row, col] }
 */
export const buildKeyPositionMap = (
  layoutData: Array<Array<number>>
): Record<string, [number, number]> => {
  const positionMap: Record<string, [number, number]> = {};

  layoutData?.forEach((row, rowIndex) => {
    row.forEach((keycode, colIndex) => {
      // 跳过空键(0)和穿透键(1)
      if (isEmptyKey(keycode)) {
        return;
      }

      const webKey = KEYCODE_TO_WEB_KEY_MAP[keycode];
      if (webKey) {
        positionMap[webKey] = [rowIndex, colIndex];
      }
    });
  });

  return positionMap;
};

/**
 * ✅ 根据键盘类型获取位置映射（动态生成）
 * 注：`"98"` 在 KEYBOARD_LAYOUTS 里为简表，与 `LAYOUT_98` / `X98_1K` 行宽不一致；
 * 贝盈 8815 的 `getLightingCustom` 使用 `X98_1K` 矩阵，若此处仍用 `"98"` 会列错位、虚拟键盘自定义色对不上。
 */
export const getPositionMapByType = (
  type: AllKeyboardType
): Record<string, [number, number]> => {
  const layout =
    type === "98"
      ? ((KEYBOARD_LAYOUTS as Record<string, number[][]>)["X98_1K"] ??
        KEYBOARD_LAYOUTS[type])
      : KEYBOARD_LAYOUTS[type];
  return buildKeyPositionMap(layout);
};

/**
 * ✅ 兼容性：65% 键盘位置映射（动态生成，保持向后兼容）
 */
export const DEFAULT_KEY_POSITION_MAP: Record<string, [number, number]> =
  getPositionMapByType("68");

/**
 * ✅ 兼容性：80% TKL 键盘位置映射（动态生成，保持向后兼容）
 */
export const TKL_KEY_POSITION_MAP: Record<string, [number, number]> =
  getPositionMapByType("87");

/** 与灯光矩阵 (row,col) 一一对应，避免同 keycode（如多颗空格 44）在 Web 键名上被覆盖 */
export const lightingMatrixKey = (row: number, col: number): string =>
  `__m_${row}_${col}`;

/** 双灯位：矩阵 (row,col) 上灯珠 */
export const lightingMatrixKeyUpper = (row: number, col: number): string =>
  `__m_${row}_${col}_up`;

/** 双灯位：矩阵 (row,col) 下灯珠 */
export const lightingMatrixKeyLower = (row: number, col: number): string =>
  `__m_${row}_${col}_dn`;

/**
 * 贝盈自定义灯固件「未设置/已清除」常回近黑非零（如 ~ 键 0,15,0；X98 计算器 15,0,16），
 * 与 ES87 F13 近黑回包同理，虚拟键盘勿铺 inline background。
 */
export const BEIYING_CUSTOM_FIRMWARE_GHOST_RGB_CEILING = 20;

export function isBeiyingCustomFirmwareGhostRgb(
  r: number,
  g: number,
  b: number
): boolean {
  const R = Number(r) || 0;
  const G = Number(g) || 0;
  const B = Number(b) || 0;
  const max = Math.max(R, G, B);
  if (max === 0) return false;
  if (max <= BEIYING_CUSTOM_FIRMWARE_GHOST_RGB_CEILING) return true;
  return false;
}

/** SDK 自定义灯网格单元 → 虚拟键盘展示格（近黑 ghost 归零） */
export function normalizeBeiyingCustomLightGridCell(
  r: number,
  g: number,
  b: number
): { R: number; G: number; B: number; isCustom: boolean } {
  const R = Number(r) || 0;
  const G = Number(g) || 0;
  const B = Number(b) || 0;
  if (isBeiyingCustomFirmwareGhostRgb(R, G, B)) {
    return { R: 0, G: 0, B: 0, isCustom: false };
  }
  const isCustom = R !== 0 || G !== 0 || B !== 0;
  return { R, G, B, isCustom };
}

/** 单元格是否含可显示的 RGB（全 0 与贝盈近黑 ghost 均视为未设置） */
export function customLightCellHasVisibleRgb(cell: KeyLightingConfig): boolean {
  const r = Number(cell.R ?? 0);
  const g = Number(cell.G ?? 0);
  const b = Number(cell.B ?? 0);
  if (r === 0 && g === 0 && b === 0) return false;
  if (isBeiyingCustomFirmwareGhostRgb(r, g, b)) return false;
  return r !== 0 || g !== 0 || b !== 0;
}

/**
 * 虚拟键盘是否渲染该灯单元：
 * - isCustom=true：用户/预览显式设色（含 #000000）
 * - isCustom=false 且 RGB 非全 0：星闪等常规灯效轮询帧
 */
export function isDisplayableLightingCell(
  cell: KeyLightingConfig | null | undefined
): cell is KeyLightingConfig {
  if (!cell || typeof cell !== "object") return false;
  if (cell.isCustom === true) return true;
  return customLightCellHasVisibleRgb(cell);
}

/**
 * 当前内页设备配置：优先 equipmentData（主应用会话），兼容旧版 app-hidGlobalStore
 */
export function readActiveEquipmentConfiguration(): Record<
  string,
  unknown
> | null {
  try {
    if (typeof window === "undefined") return null;
    const equipmentRaw =
      window.sessionStorage.getItem("eweadn:session:equipmentData") ??
      window.localStorage.getItem("equipmentData");
    if (equipmentRaw) {
      return JSON.parse(equipmentRaw) as Record<string, unknown>;
    }
    const hidRaw = window.localStorage.getItem("app-hidGlobalStore");
    if (hidRaw) {
      const parsed = JSON.parse(hidRaw) as {
        state?: { configurationNow?: Record<string, unknown> };
      };
      return parsed?.state?.configurationNow ?? null;
    }
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * 二维数组 → KeyColors 映射
 */
export const customDataToKeyColors = (
  data: Array<Array<KeyLightingConfig>> | null | undefined,
  positionMap: Record<string, [number, number]> = DEFAULT_KEY_POSITION_MAP
): Record<string, KeyLightingConfig> => {
  const keyColors: Record<string, KeyLightingConfig> = {};
  if (!data || !Array.isArray(data)) {
    return keyColors;
  }
  Object.entries(positionMap).forEach(([code, pos]) => {
    if (!pos) return;
    const [row, col] = pos;
    const cell = data[row]?.[col];
    if (
      cell != null &&
      typeof cell === "object" &&
      isDisplayableLightingCell(cell)
    ) {
      keyColors[code] = cell;
    }
  });

  // 按矩阵写入，供 UI 按 (layoutRow, originalCol) 精确取色（分裂空格等）
  data?.forEach((row, rowIndex) => {
    row?.forEach((cell, colIndex) => {
      if (
        cell != null &&
        typeof cell === "object" &&
        isDisplayableLightingCell(cell)
      ) {
        keyColors[lightingMatrixKey(rowIndex, colIndex)] = cell;
      }
    });
  });

  return keyColors;
};

/**
 * 贝盈自定义灯：按 layout 矩阵 keycode 生成虚拟键盘 keyColors 补丁（设置成功后乐观回显）
 */
export const buildBeiyingCustomLightKeyColorsPatch = (
  layoutKeycodes: number[],
  color: { r: number; g: number; b: number },
  keyboardType: string,
  equipment?: {
    solutionRovider?: string;
    displayname?: string;
    keyboardType?: string;
  } | null
): Record<string, KeyLightingConfig> => {
  const matrix = resolveBeiyingCustomLightLayoutMatrix(keyboardType, equipment);
  if (!matrix?.length) return {};

  const codesToPaint = new Set<number>();
  for (const lc of layoutKeycodes) {
    if (!Number.isFinite(lc)) continue;
    codesToPaint.add(lc);
    if (lc === 21252) codesToPaint.add(101);
    const cap = KEYCODE_MAP_BEIYING.find(
      (k) => k.keycode === lc || k.xsKeycode === lc
    );
    if (cap?.keycode != null) codesToPaint.add(cap.keycode);
    if (cap?.xsKeycode != null) codesToPaint.add(cap.xsKeycode);
  }

  const cell: KeyLightingConfig = {
    R: color.r,
    G: color.g,
    B: color.b,
    isCustom: true,
  };
  const patch: Record<string, KeyLightingConfig> = {};

  matrix.forEach((row, rowIndex) => {
    row.forEach((code, colIndex) => {
      if (!code || !codesToPaint.has(code)) return;
      patch[lightingMatrixKey(rowIndex, colIndex)] = cell;
      const webKey = KEYCODE_TO_WEB_KEY_MAP[code];
      if (webKey) patch[webKey] = cell;
    });
  });

  return patch;
};

/**
 * 华奋达自定义灯：按 keyIndex(ledId) 生成虚拟键盘 keyColors 补丁（选键/设色后立即回显）
 */
export const buildHfdCustomLightKeyColorsPatch = (
  keyIndices: number[],
  color: { r: number; g: number; b: number },
  keyboardType: string
): Record<string, KeyLightingConfig> => {
  const layoutKey =
    (keyboardType as keyof typeof KEYCODE_MAP_HFD_LAYOUT) || "huafenda87";
  const layoutData =
    KEYCODE_MAP_HFD_LAYOUT[layoutKey] ?? KEYCODE_MAP_HFD_LAYOUT.huafenda87;
  if (!layoutData?.length) return {};

  const keyIndexSet = new Set(keyIndices.filter((idx) => Number.isFinite(idx)));
  if (!keyIndexSet.size) return {};

  const cell: KeyLightingConfig = {
    R: color.r,
    G: color.g,
    B: color.b,
    isCustom: true,
  };
  const patch: Record<string, KeyLightingConfig> = {};

  for (let rowIndex = 0; rowIndex < layoutData.length; rowIndex++) {
    const row = layoutData[rowIndex];
    for (let colIndex = 0; colIndex < row.length; colIndex++) {
      const slotKeycode = row[colIndex];
      if (!slotKeycode) continue;
      const keyIndex = KEYCODE_MAP_HFD.find(
        (item) => item.keycode === slotKeycode
      )?.keyIndex;
      if (keyIndex == null || !keyIndexSet.has(keyIndex)) continue;
      patch[lightingMatrixKey(rowIndex, colIndex)] = cell;
      const webKey = KEYCODE_TO_WEB_KEY_MAP[slotKeycode];
      if (webKey) patch[webKey] = cell;
    }
  }

  return patch;
};

/** 轮询回显与乐观 patch 合并：patch 中非零色优先，避免 refresh 后漏键变黑 */
export const mergeBeiyingKeyColorsWithPatch = (
  base: Record<string, KeyLightingConfig>,
  patch: Record<string, KeyLightingConfig>
): Record<string, KeyLightingConfig> => {
  if (!Object.keys(patch).length) return base;
  const merged = { ...base };
  for (const [k, patchCell] of Object.entries(patch)) {
    if (!patchCell || !customLightCellHasVisibleRgb(patchCell)) continue;
    merged[k] = patchCell;
  }
  return merged;
};

/**
 * KeyColors → 二维数组映射
 */
export const keyColorsToCustomData = (
  keyColors: Record<string, KeyLightingConfig>,
  rows: number = 6,
  cols: number = 17,
  positionMap: Record<string, [number, number]> = DEFAULT_KEY_POSITION_MAP
): Array<Array<KeyLightingConfig>> => {
  const data: Array<Array<KeyLightingConfig>> = Array(rows)
    .fill(null)
    .map(() =>
      Array(cols)
        .fill(null)
        .map(() => ({
          R: 0,
          G: 0,
          B: 0,
          isCustom: false,
        }))
    );

  Object.entries(keyColors).forEach(([code, color]) => {
    const position = positionMap[code];
    if (position) {
      const [row, col] = position;
      if (row < rows && col < cols) {
        data[row][col] = color;
      }
    }
  });

  return data;
};
